// portal-web-ui/src/hooks/useReportAnalytics.ts
import { isReviewItemResolved } from "@utils/reportVisibility";
import { useCallback, useEffect, useRef } from "react";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { useAdminData } from "@providers/AdminDataProvider";
import { useAnalytics } from "@providers/AnalyticsProvider";

export type ReportType = "initial_assessment" | "reassessment" | "diagnosis_builder";

export interface ReportContext {
  report_id: string;
  report_type: ReportType;
  template_id?: string;
  template_name?: string;
  template_payor_type?: string;
  template_export_framework?: string;
}

export interface ReportCompletedExtras {
  total_days_to_complete?: number;
  // Count of unresolved Insurance Standards QA suggestions at completion time.
  // Drives PostHog's pre-configured "High-Risk Completion" Action.
  insurance_flags_remaining?: number;
}

const MS_PER_DAY = 86_400_000;

export function computeTotalDaysToComplete(createdDate: string | null | undefined): number | undefined {
  if (!createdDate) return undefined;
  const ms = new Date(createdDate).getTime();
  if (Number.isNaN(ms)) return undefined;
  return Math.floor((Date.now() - ms) / MS_PER_DAY);
}

// QA suggestion `category` arrives in two shapes:
//   - legacy SCREAMING_SNAKE_CASE (e.g. "INSURANCE_STANDARDS")
//   - QA-package title-case (e.g. "Insurance Standards")
// Normalize both to "insurance standards" for matching.
export function countInsuranceFlagsRemaining(items: ReviewItemFieldsFragment[] | undefined): number | undefined {
  if (!items) return undefined;
  return items.reduce((count, item) => {
    if (item.__typename !== "ReportSuggestionType" || isReviewItemResolved(item)) return count;
    const normalized = item.category.replace(/_/g, " ").toLowerCase();
    return normalized === "insurance standards" ? count + 1 : count;
  }, 0);
}

export function useReportAnalytics(ctx: ReportContext) {
  const analytics = useAnalytics();
  const { orgId, orgName } = useAdminData();

  // Destructure so callbacks reference stable individual values, not the ctx object reference.
  const { report_id, report_type, template_id, template_name, template_payor_type, template_export_framework } = ctx;

  const lastFiredReportId = useRef<string | null>(null);

  // Groups re-attach idempotently; only the event itself is deduped per report_id.
  const captureReportOpened = useCallback(() => {
    if (!analytics) return;
    if (!orgId) return; // wait until AdminDataProvider has resolved
    if (!report_id) return;

    analytics.group("product_group", report_type);
    if (template_id) {
      analytics.group("template", template_id, {
        name: template_name,
        report_type,
        payor_type: template_payor_type,
        export_framework: template_export_framework,
        org_id: orgId,
      });
    }

    if (lastFiredReportId.current !== report_id) {
      analytics.capture("report_opened", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
      });
      lastFiredReportId.current = report_id;
    }
  }, [
    analytics,
    orgId,
    orgName,
    report_id,
    report_type,
    template_id,
    template_name,
    template_payor_type,
    template_export_framework,
  ]);

  const captureWizardStepAdvanced = useCallback(
    (from_step: string, to_step: string, time_on_step_seconds: number) =>
      analytics?.capture("wizard_step_advanced", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        from_step,
        to_step,
        time_on_step_seconds,
      }),
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  const captureWizardStepBack = useCallback(
    (from_step: string, to_step: string, time_on_step_seconds: number) =>
      analytics?.capture("wizard_step_back", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        from_step,
        to_step,
        time_on_step_seconds,
      }),
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  // `$set` updates person properties as part of the event — equivalent to
  // re-calling identify() with last_report_completed_at, per Grant's guide.
  // True lifetime count is deferred to a backend aggregate (separate ticket);
  // dashboards can compute count(report_completed) per distinct_id in the meantime.
  const captureReportCompleted = useCallback(
    (extras?: ReportCompletedExtras) =>
      analytics?.capture("report_completed", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        ...extras,
        $set: { last_report_completed_at: new Date().toISOString() },
      }),
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  const captureGoalApproved = useCallback(
    (goal_id: string, domain_id?: string, attempts_before_success?: number) => {
      if (!report_id) return;
      analytics?.capture("goal_approved", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        goal_id,
        domain_id,
        attempts_before_success,
      });
    },
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  const captureGoalApproveAttempted = useCallback(
    (
      goal_id: string,
      props: {
        attempt_number: number;
        validation_errors: string[];
        chart_ready: boolean;
        has_targets: boolean;
        domain_id?: string;
      },
    ) => {
      if (!report_id) return;
      analytics?.capture("goal_approve_attempted", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        goal_id,
        ...props,
      });
    },
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  const captureGoalApprovalRevoked = useCallback(
    (goal_id: string, domain_id?: string) => {
      if (!report_id) return;
      analytics?.capture("goal_approval_revoked", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        goal_id,
        domain_id,
      });
    },
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  // goal_count / target_count are the unapproved counts at the time of the action;
  // successes is the server-confirmed approved count (may be less if some failed).
  const captureApproveAll = useCallback(
    (goal_count: number, target_count: number, successes: number) =>
      analytics?.capture("goal_approve_all", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        goal_count,
        target_count,
        successes,
      }),
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  const captureGoalSidebarSelect = useCallback(
    (goal_id: string, domain_id?: string) =>
      analytics?.capture("goal_sidebar_select", {
        report_id,
        report_type,
        template_id,
        template_name,
        template_payor_type,
        template_export_framework,
        org_id: orgId,
        org_name: orgName,
        goal_id,
        domain_id,
      }),
    [
      analytics,
      orgId,
      orgName,
      report_id,
      report_type,
      template_id,
      template_name,
      template_payor_type,
      template_export_framework,
    ],
  );

  return {
    captureReportOpened,
    captureWizardStepAdvanced,
    captureWizardStepBack,
    captureReportCompleted,
    captureGoalApproved,
    captureGoalApprovalRevoked,
    captureApproveAll,
    captureGoalSidebarSelect,
    captureGoalApproveAttempted,
  };
}

export function useTrackReportOpened(reportContext: ReportContext): void {
  const { captureReportOpened } = useReportAnalytics(reportContext);

  useEffect(() => {
    if (reportContext.report_id) captureReportOpened();
  }, [reportContext.report_id, reportContext.template_id, captureReportOpened]);
}
